// frontend/src/components/SideBar.js
import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { NavLink, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMd, faBrain, faBook, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import './SideBar.css';
import defaultAvatar from '../assets/default-profile.png';
import quironLogo from '../assets/quiron-isotipo.png';
import { useTranslation } from 'react-i18next';

const API_STATIC_FILES_URL = process.env.REACT_APP_API_URL_FOR_STATIC_FILES || 'https://quiron.app';
const SIDEBAR_TRANSITION_DURATION = 300; // ms, deve corresponder à transição de 'width' no CSS

function SideBar({ onLogout, isOpen, toggleSidebar }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [showText, setShowText] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setShowText(true);
      }, SIDEBAR_TRANSITION_DURATION);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setShowText(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen]);

  let profilePictureSrc = defaultAvatar;
  if (user && user.profile_picture) {
    if (user.profile_picture.startsWith('http')) {
      profilePictureSrc = user.profile_picture;
    } else if (user.profile_picture !== 'default-profile.png') {
      profilePictureSrc = `${API_STATIC_FILES_URL}/static/uploads/profile_pictures/${user.profile_picture}`;
    }
  }

  const profileLinkText = isOpen ? t('profile') : '';

  const renderLinkText = (translationKey) => {
    if (isOpen) {
      return (
        <span key={translationKey} className={`link-text ${showText ? 'text-visible' : ''}`}>
          {t(translationKey)}
        </span>
      );
    }
    return null;
  };
  
  const renderProfileLinkText = () => {
    if (isOpen) {
      return (
        <span key="profile-text" className={`link-text ${showText ? 'text-visible' : ''}`}>
          {profileLinkText}
        </span>
      );
    }
    return null;
  }

  return (
    <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <button className="sidebar-toggle-internal" onClick={toggleSidebar} title={isOpen ? t('closeSidebar') : t('openSidebar')}>
          {isOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18" className="sidebar-toggle-icon">
              <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1" fill="none" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 9l-2 3 2 3" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18" className="sidebar-toggle-icon">
              <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1" fill="none" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 9l2 3-2 3" />
            </svg>
          )}
        </button>
        <Link to="/" className="sidebar-logo-link">
          <img src={quironLogo} alt="Quiron Logo" className="sidebar-logo-img" />
          {isOpen && (
            <span className={`sidebar-logo-text ${showText ? 'text-visible' : ''}`}>
              Quiron
            </span>
          )}
        </Link>
      </div>
      <ul className="navigation-links">
        <li>
          <NavLink to="/consultation-manager" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <FontAwesomeIcon icon={faUserMd} className="sidebar-icon" />
            {renderLinkText('consultationManager')}
          </NavLink>
        </li>
        <li>
          <NavLink to="/neuralweb" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <FontAwesomeIcon icon={faBrain} className="sidebar-icon" />
            {renderLinkText('neuralWeb')}
          </NavLink>
        </li>
        <li>
          <NavLink to="/academic" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <FontAwesomeIcon icon={faBook} className="sidebar-icon" />
            {renderLinkText('academic')}
          </NavLink>
        </li>
      </ul>
      <div className="sidebar-footer">
        <div className="separator"></div>
        <Link to="/profile" className="profile-link sidebar-link">
          <img
            src={profilePictureSrc}
            alt={t('profile')}
            className="profile-icon sidebar-icon"
            onError={(e) => {
              console.warn('Erro ao carregar imagem de perfil na sidebar, usando padrão:', e.target.src);
              e.target.onerror = null; 
              e.target.src = defaultAvatar;
            }}
          />
          {renderProfileLinkText()}
        </Link>
        <button onClick={onLogout} className="logout-button sidebar-link">
          <FontAwesomeIcon icon={faSignOutAlt} className="sidebar-icon" />
          {isOpen && (
            <span key="logout-text" className={`link-text ${showText ? 'text-visible' : ''}`}>
              {t('logout')}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

export default SideBar;